import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, FileCheck2, FileUp, MapPin, RefreshCw } from 'lucide-react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { formatMoney } from '@/lib/utils'

import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { quoteQueryOptions, workflowQueryOptions } from '../api/queries'
import { AiData } from '../ui/ai-data'
import { ErrorState, LoadingState, SurfaceCard } from '../ui/page'
import type { WorkflowTabProps } from '../workflow/types'
import { formatTaxRate } from './tax-format'
import {
  TaxEvidenceError,
  taxesApi,
  taxesQueryOptions,
  type TaxEvidence,
  type TaxSettings,
  type TaxWorkspace,
} from './taxes-api'

const maximumFileSize = 10 * 1024 * 1024
const acceptedTypes = new Set(['application/pdf', 'image/png', 'image/jpeg'])
const dateTime = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' })
const categoryKeys = ['equipment_taxable', 'shipping_taxable', 'services_taxable', 'other_taxable'] as const
const categoryLabels: Record<(typeof categoryKeys)[number], string> = {
  equipment_taxable: 'Equipment',
  shipping_taxable: 'Shipping',
  services_taxable: 'Services',
  other_taxable: 'Other charges',
}
const fieldLabels: Record<string, string> = {
  document_type: 'Document',
  certificate_holder: 'Holder',
  certificate_number: 'Number',
  issued_state: 'State',
  effective_date: 'Effective',
}
type LineChoice = 'default' | 'taxable' | 'exempt'

function readableLabel(value: string) {
  return value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
}

function parseUTC(value: string) {
  return new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`)
}

function settingsOf(workspace: TaxWorkspace): TaxSettings {
  return { ...workspace.settings, line_overrides: { ...(workspace.settings.line_overrides ?? {}) } }
}

function EvidenceRow({ evidence, onOpen }: { evidence: TaxEvidence; onOpen: () => void }) {
  const fields = Object.entries(evidence.extracted_fields ?? {}).filter(([, value]) => value)
  const exemptions = Object.entries(evidence.suggested_exemptions ?? {}).filter(([, exempt]) => exempt)
  const readable = evidence.status === 'parsed'
  return (
    <article className="border-t px-4 py-4 first:border-t-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <FileCheck2 aria-hidden="true" className="size-4 text-muted-foreground" />
            <button
              className="truncate text-sm font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              disabled={evidence.status === 'awaiting_upload'}
              onClick={onOpen}
              type="button"
            >
              {evidence.original_file_name}
            </button>
            <StatusPill tone={readable ? 'warning' : evidence.status === 'parse_failed' ? 'danger' : 'neutral'}>
              {readable ? 'Review required' : evidence.status === 'parse_failed' ? 'Not read' : 'Uploading'}
            </StatusPill>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {dateTime.format(parseUTC(evidence.parsed_at ?? evidence.created_at))} · {(evidence.size_bytes / 1024).toFixed(1)} KB
          </p>
        </div>
        {evidence.confidence != null ? (
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            Read with {Math.round(Number(evidence.confidence) * 100)}% confidence
          </span>
        ) : null}
      </div>
      {readable ? (
        <AiData className="mt-3 p-3" label="Fields read from the certificate">
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {fields.map(([key, value]) => (
              <div className="flex min-w-0 items-baseline justify-between gap-3 border-b pb-1.5" key={key}>
                <dt className="text-xs text-muted-foreground">{fieldLabels[key] ?? readableLabel(key)}</dt>
                <dd className="min-w-0 truncate text-right text-xs font-medium">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-muted-foreground">Suggested exemptions</span>
            {exemptions.length ? (
              exemptions.map(([name]) => (
                <StatusPill key={name} tone="neutral">
                  {readableLabel(name)}
                </StatusPill>
              ))
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </div>
        </AiData>
      ) : null}
    </article>
  )
}

/** The Tax stage, ported from the demo's `TaxesTab`. */
export function TaxesTab({ active = true, quoteId, quote, workflow, onQuoteChange, onWorkflowChange }: WorkflowTabProps) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const options = taxesQueryOptions(api, quoteId)
  const cached = queryClient.getQueryData(options.queryKey)
  const fileInput = useRef<HTMLInputElement>(null)
  const [workspace, setWorkspace] = useState<TaxWorkspace | null>(cached ?? null)
  const [settings, setSettings] = useState<TaxSettings | null>(cached ? settingsOf(cached) : null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'apply' | 'upload' | 'override' | null>(null)
  const [error, setError] = useState('')
  const [saveFailed, setSaveFailed] = useState(false)
  const [success, setSuccess] = useState('')
  const [conflict, setConflict] = useState(false)
  const [suggestionEvidenceId, setSuggestionEvidenceId] = useState<string | null>(null)
  const [evidenceRetry, setEvidenceRetry] = useState<{ file: File; failure: TaxEvidenceError } | null>(null)
  const draft = useRef<TaxSettings | null>(cached ? settingsOf(cached) : null)
  const edits = useRef(new Map<string, number>())
  const editVersion = useRef(0)
  const readSequence = useRef(0)
  const operation = useRef(0)
  const latestQuote = useRef(quote)
  latestQuote.current = quote.revision >= latestQuote.current.revision ? quote : latestQuote.current
  const savedRefresh = useRef<'apply' | 'upload' | null>(null)
  const [refreshNeeded, setRefreshNeeded] = useState(false)
  const workspaceQuoteId = useRef<string | null>(quoteId)

  function editSettings(next: TaxSettings, fields: string[]) {
    for (const field of fields) edits.current.set(field, ++editVersion.current)
    draft.current = next
    setSettings(next)
    setSuccess('')
  }

  async function refresh(signal?: AbortSignal, reloadQuote = false) {
    // Keep saved calculations authoritative while retaining each newer local field edit.
    const sequence = ++readSequence.current
    const revision = latestQuote.current.revision
    const ownsRead = () => sequence === readSequence.current && !signal?.aborted && latestQuote.current.id === quoteId
    const current = () => ownsRead() && latestQuote.current.revision === revision
    setLoading(true)
    try {
      const stale = reloadQuote || (queryClient.getQueryData(options.queryKey)?.quote_revision ?? 0) < revision
      const [first, nextWorkflow, nextQuote] = await Promise.all([
        queryClient.fetchQuery({ ...options, staleTime: stale ? 0 : 30_000 }),
        reloadQuote ? queryClient.fetchQuery({ ...workflowQueryOptions(api, quoteId), staleTime: 0 }) : null,
        reloadQuote ? queryClient.fetchQuery({ ...quoteQueryOptions(api, quoteId), staleTime: 0 }) : null,
      ])
      if (!current()) return false
      const next =
        first.quote_revision < revision ? await queryClient.fetchQuery({ ...options, staleTime: 0 }) : first
      if (!current() || next.quote_revision < revision) return false
      const merged = settingsOf(next)
      const local = draft.current
      if (local) {
        for (const key of categoryKeys) if (edits.current.has(key)) merged[key] = local[key]
        const lineIds = new Set(next.calculation.line_breakdown.map((line) => line.line_id))
        const overrides = merged.line_overrides ?? {}
        for (const key of [...edits.current.keys()]) {
          if (!key.startsWith('line:')) continue
          const id = key.slice(5)
          if (!lineIds.has(id)) {
            edits.current.delete(key)
            continue
          }
          const value = local.line_overrides?.[id]
          if (value === undefined) delete overrides[id]
          else overrides[id] = value
        }
        merged.line_overrides = overrides
      }
      draft.current = merged
      setWorkspace(next)
      setSettings(merged)
      setRefreshNeeded(false)
      setError(conflict && !savedRefresh.current ? 'This quote changed. Review your kept edits and save again.' : '')
      if (savedRefresh.current) {
        setSuccess(savedRefresh.current === 'upload' ? 'Certificate suggestions are ready to review.' : 'Taxability updated.')
        savedRefresh.current = null
      }
      if (nextQuote && nextQuote.revision >= latestQuote.current.revision) onQuoteChange(nextQuote)
      if (nextWorkflow) onWorkflowChange(nextWorkflow)
      return true
    } catch {
      if (current()) {
        setRefreshNeeded(true)
        setError(
          savedRefresh.current === 'upload'
            ? 'Certificate uploaded. Refresh to load its review.'
            : savedRefresh.current
              ? 'Tax changes were saved. Refresh to load the updated totals.'
              : 'Taxes could not refresh. Try again.',
        )
      }
      return false
    } finally {
      if (ownsRead()) setLoading(false)
    }
  }

  useEffect(() => {
    if (!active) return
    const controller = new AbortController()
    if (workspaceQuoteId.current !== quoteId) {
      workspaceQuoteId.current = quoteId
      ++operation.current
      draft.current = null
      edits.current.clear()
      savedRefresh.current = null
      setWorkspace(null)
      setSettings(null)
      setBusy(null)
      setError('')
      setSuccess('')
      setConflict(false)
      setRefreshNeeded(false)
      setSuggestionEvidenceId(null)
      setSaveFailed(false)
      setEvidenceRetry(null)
    }
    void refresh(controller.signal)
    return () => controller.abort()
    // The demo reloads on the stage becoming active, a new quote, or a new revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, quoteId, quote.revision])

  useEffect(
    () => () => {
      ++operation.current
      ++readSequence.current
    },
    [],
  )

  async function retryInitialLoad() {
    await queryClient.invalidateQueries({ queryKey: options.queryKey })
    await refresh(undefined, conflict)
  }

  function populateSuggestions(evidence: TaxEvidence) {
    if (!evidence.suggested_exemptions || !draft.current) return
    editSettings(
      {
        ...draft.current,
        equipment_taxable: !evidence.suggested_exemptions.equipment,
        shipping_taxable: !evidence.suggested_exemptions.shipping,
        services_taxable: !evidence.suggested_exemptions.services,
      },
      ['equipment_taxable', 'shipping_taxable', 'services_taxable'],
    )
    setSuggestionEvidenceId(evidence.id)
  }

  async function processFile(file: File, resume?: TaxEvidenceError) {
    if (busy) return
    const extensionAllowed = /\.(pdf|png|jpe?g)$/i.test(file.name)
    if (!acceptedTypes.has(file.type) || !extensionAllowed || file.size > maximumFileSize) {
      setError('Choose a PDF, PNG, or JPEG no larger than 10 MB.')
      if (fileInput.current) fileInput.current.value = ''
      return
    }
    const token = ++operation.current
    const current = () => token === operation.current && latestQuote.current.id === quoteId
    setBusy('upload')
    setError('')
    setSuccess('')
    setEvidenceRetry(null)
    try {
      const evidence = await taxesApi(api).createEvidence(quoteId, file, resume)
      if (!current()) return
      setWorkspace((value) => (value ? { ...value, evidence: [...value.evidence.filter((item) => item.id !== evidence.id), evidence] } : value))
      populateSuggestions(evidence)
      savedRefresh.current = 'upload'
      await queryClient.invalidateQueries({ queryKey: options.queryKey })
      if ((await refresh()) && current()) setSuccess('Certificate suggestions are ready to review.')
    } catch (reason) {
      if (current()) {
        if (reason instanceof TaxEvidenceError) {
          setEvidenceRetry({ file, failure: reason })
          setWorkspace((value) =>
            value
              ? { ...value, evidence: [...value.evidence.filter((item) => item.id !== reason.evidence.id), reason.evidence] }
              : value,
          )
        } else setError('The certificate could not be started. Choose the file and try again.')
      }
    } finally {
      if (current()) {
        setBusy(null)
        if (fileInput.current) fileInput.current.value = ''
      }
    }
  }

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) void processFile(file)
  }

  async function openEvidence(evidence: TaxEvidence) {
    const opened = window.open('', '_blank')
    try {
      const blob = await taxesApi(api).download(quoteId, evidence.id)
      const url = URL.createObjectURL(blob)
      if (opened) opened.location.href = url
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      opened?.close()
      setError('The certificate could not be opened. Try again.')
    }
  }

  async function apply() {
    if (!draft.current || busy) return
    const submitted = draft.current
    const submittedEdits = new Map(edits.current)
    const token = ++operation.current
    const current = () => token === operation.current && latestQuote.current.id === quoteId
    setBusy('apply')
    setError('')
    setSaveFailed(false)
    setSuccess('')
    setConflict(false)
    try {
      ++readSequence.current
      await queryClient.cancelQueries({ queryKey: options.queryKey })
      const revised = suggestionEvidenceId
        ? await taxesApi(api).applyEvidence(quoteId, latestQuote.current.revision, suggestionEvidenceId, submitted)
        : await taxesApi(api).update(quoteId, latestQuote.current.revision, submitted)
      if (!current()) return
      // Acknowledging the submitted edit must not acknowledge another edit made during save.
      for (const [key, version] of submittedEdits) if (edits.current.get(key) === version) edits.current.delete(key)
      savedRefresh.current = 'apply'
      ++readSequence.current
      if (revised.revision >= latestQuote.current.revision) {
        latestQuote.current = revised
        onQuoteChange(revised)
      }
      setWorkspace((value) => (value ? { ...value, settings: submitted } : value))
      setSuccess('Taxability updated.')
      setSuggestionEvidenceId(null)
      void refresh()
    } catch (reason) {
      if (!current()) return
      setSaveFailed(true)
      if (reason instanceof QuoteBuilderApiError && reason.status === 412) {
        setConflict(true)
        if ((await refresh(undefined, true)) && current()) setError('This quote changed. Review your kept edits and save again.')
      } else setError('Taxability could not be updated. Review your changes and try again.')
    } finally {
      if (current()) setBusy(null)
    }
  }

  async function saveOverride(taxOverride: string | null) {
    if (!draft.current || busy) return
    const token = ++operation.current
    const current = () => token === operation.current && latestQuote.current.id === quoteId
    setBusy('override')
    setError('')
    setSuccess('')
    try {
      ++readSequence.current
      await queryClient.cancelQueries({ queryKey: options.queryKey })
      const revised = await taxesApi(api).setOverride(quoteId, latestQuote.current.revision, draft.current, taxOverride)
      if (!current()) return
      if (revised.revision >= latestQuote.current.revision) {
        latestQuote.current = revised
        onQuoteChange(revised)
      }
      savedRefresh.current = 'apply'
      setSuccess(taxOverride === null ? 'Tax follows the calculation again.' : 'Tax amount saved.')
      void refresh(undefined, true)
    } catch (reason) {
      if (!current()) return
      if (reason instanceof QuoteBuilderApiError && reason.status === 412) {
        setConflict(true)
        if ((await refresh(undefined, true)) && current()) setError('This quote changed. Enter the tax amount again.')
      } else setError('The tax amount could not be saved. Try again.')
    } finally {
      if (current()) setBusy(null)
    }
  }

  useEffect(() => {
    if (!edits.current.size || busy || saveFailed || suggestionEvidenceId) return
    const timer = window.setTimeout(() => void apply(), 400)
    return () => window.clearTimeout(timer)
    // The demo autosaves 400ms after the last edit unless certificate suggestions await review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, busy, saveFailed, suggestionEvidenceId])

  if (loading && !workspace) return <LoadingState label="Loading taxes" />
  if (!workspace || !settings) {
    return (
      <SurfaceCard>
        <ErrorState
          description={error || 'The tax stage could not be loaded.'}
          onRetry={() => void retryInitialLoad()}
          title="Taxes unavailable"
        />
      </SurfaceCard>
    )
  }

  const { calculation, approval_summary: summary } = workspace
  const jurisdiction = calculation.jurisdiction
  const latest = workspace.latest_jurisdiction
  const workflowStage = workflow.stages.find((stage) => stage.key === 'tax')
  const approvalReady = summary.status === 'ready' && workflowStage?.status === 'complete'
  const disabled = busy !== null
  const editingDisabled = busy === 'upload'
  const overrides = settings.line_overrides ?? {}
  const hasDraftChanges = JSON.stringify(settings) !== JSON.stringify(settingsOf(workspace))
  const setAllTaxable = (taxable: boolean) => {
    editSettings(
      {
        equipment_taxable: taxable,
        shipping_taxable: taxable,
        services_taxable: taxable,
        other_taxable: taxable,
        // Clearing exceptions lets every current and newly added line follow the bulk rule.
        line_overrides: {},
      },
      [
        ...categoryKeys,
        ...calculation.line_breakdown.map((line) => `line:${line.line_id}`),
        ...Object.keys(overrides).map((id) => `line:${id}`),
      ],
    )
  }
  const setCategory = (key: (typeof categoryKeys)[number], value: boolean) => {
    if (draft.current) editSettings({ ...draft.current, [key]: value }, [key])
  }
  const setLineOverride = (lineId: string, value: LineChoice) => {
    if (!draft.current) return
    const lineOverrides = { ...(draft.current.line_overrides ?? {}) }
    if (value === 'default') delete lineOverrides[lineId]
    else lineOverrides[lineId] = value === 'taxable'
    editSettings({ ...draft.current, line_overrides: lineOverrides }, [`line:${lineId}`])
  }
  const breakdown: Array<[string, string]> = [
    ['Equipment', calculation.equipment_subtotal],
    ['Services', calculation.services_subtotal],
    ['Shipping', calculation.freight],
    ['Other', calculation.other_subtotal],
    ['Taxable', calculation.taxable_basis],
    ['Exempt', calculation.exempt_basis],
  ]

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_310px]">
      <div className="min-w-0 space-y-4">
        <SurfaceCard
          title={
            hasDraftChanges || busy === 'apply'
              ? 'Saved tax · Changes pending'
              : loading || refreshNeeded
                ? 'Saved tax · Updating totals'
                : 'Calculated tax'
          }
        >
          {workflowStage?.detail && workflowStage.status !== 'complete' ? (
            <div className="flex items-center justify-between gap-3 border-b bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
              <span>{workflowStage.detail}</span>
              <StatusPill tone="warning">Needs attention</StatusPill>
            </div>
          ) : null}
          <AiData className="m-3" label="Calculated sales tax">
            <div className="grid gap-4 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
              <div className="flex min-w-0 gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
                  <MapPin aria-hidden="true" className="size-4" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{jurisdiction.jurisdiction}</p>
                    {jurisdiction.status === 'ready' ? (
                      <span className="text-sm tabular-nums text-muted-foreground">{formatTaxRate(jurisdiction.rate)}</span>
                    ) : null}
                    <StatusPill tone={jurisdiction.status === 'ready' ? 'success' : 'warning'}>
                      {jurisdiction.status === 'ready' ? 'Calculated' : 'Review needed'}
                    </StatusPill>
                  </div>
                  <p className="mt-1 break-words text-sm text-muted-foreground">{jurisdiction.address || 'No delivery address'}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{jurisdiction.message}</p>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs font-medium text-muted-foreground">Tax</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums">{formatMoney(calculation.tax)}</p>
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground sm:justify-end">
                  <span>{calculation.tax_override_source === 'aqproj' ? 'From AQProj' : 'Override'}</span>
                  <POInlineEditableField
                    align="right"
                    ariaLabel="Tax override"
                    disabled={disabled}
                    displayValue={calculation.tax_override != null ? formatMoney(calculation.tax_override) : 'None'}
                    kind="money"
                    onConfirm={(raw) => {
                      const value = raw.trim() === '' ? null : Number(raw)
                      if (value != null && (!Number.isFinite(value) || value < 0)) return
                      void saveOverride(value == null ? null : value.toFixed(2))
                    }}
                    pending={busy === 'override'}
                    showEditIcon
                    value={calculation.tax_override ?? null}
                  />
                </div>
                {calculation.tax_override != null ? (
                  <Button
                    className="mt-1 h-7 px-2 text-xs"
                    disabled={disabled}
                    onClick={() => void saveOverride(null)}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    Use calculated {formatMoney(calculation.calculated_tax)}
                  </Button>
                ) : null}
              </div>
            </div>
            <dl className="grid gap-x-8 gap-y-2 border-t p-4 text-sm sm:grid-cols-2">
              {breakdown.map(([label, value]) => (
                <div className="flex justify-between gap-4" key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="font-semibold tabular-nums">{formatMoney(value)}</dd>
                </div>
              ))}
            </dl>
          </AiData>
          {latest ? (
            <div className="flex flex-col gap-2 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" role="status">
              <span>
                New rate for this address: <strong className="font-semibold">{latest.jurisdiction}</strong>
                {latest.status === 'ready' ? ` ${formatTaxRate(latest.rate)}` : ''}. Saving applies it.
              </span>
              <LoadingButton loading={busy === 'apply'} loadingText="Saving..." onClick={() => void apply()} size="sm" variant="outline">
                Apply new rate
              </LoadingButton>
            </div>
          ) : null}
        </SurfaceCard>

        <SurfaceCard title="Taxability">
          <div className="flex flex-wrap gap-2 border-b p-4">
            <Button disabled={editingDisabled} onClick={() => setAllTaxable(true)} type="button" variant="outline">
              Mark all taxable
            </Button>
            <Button disabled={editingDisabled} onClick={() => setAllTaxable(false)} type="button" variant="outline">
              Mark all non-taxable
            </Button>
            <LoadingButton
              className="w-full sm:ml-auto sm:w-auto"
              disabled={disabled}
              loading={busy === 'apply'}
              loadingText="Saving..."
              onClick={() => void apply()}
              type="button"
            >
              <Check aria-hidden="true" />
              Save & calculate
            </LoadingButton>
          </div>
          {error ? (
            <div
              className="flex flex-col gap-2 border-b border-destructive/20 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive sm:flex-row sm:items-center"
              role="alert"
            >
              <span className="flex min-w-0 items-center gap-2">
                <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
                {error}
              </span>
              {refreshNeeded ? (
                <Button className="sm:ml-auto" disabled={busy !== null || loading} onClick={() => void retryInitialLoad()} type="button" variant="outline">
                  <RefreshCw aria-hidden="true" />
                  Retry refresh
                </Button>
              ) : conflict ? (
                <Button className="sm:ml-auto" disabled={disabled} onClick={() => void apply()} type="button" variant="outline">
                  <RefreshCw aria-hidden="true" />
                  Retry save
                </Button>
              ) : null}
            </div>
          ) : null}
          {success && (!hasDraftChanges || suggestionEvidenceId) ? (
            <div className="flex items-center gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" role="status">
              <Check aria-hidden="true" className="size-4" />
              {success}
            </div>
          ) : null}
          <fieldset className="p-4">
            <legend className="text-xs font-medium text-muted-foreground">Category rules</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {categoryKeys.map((key) => (
                <label
                  className="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-lg border bg-background px-3 text-sm font-medium has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50"
                  key={key}
                >
                  {categoryLabels[key]}
                  <Switch checked={settings[key]} disabled={editingDisabled} onCheckedChange={(value) => setCategory(key, value)} />
                </label>
              ))}
            </div>
          </fieldset>
          <div className="border-t">
            <div className="hidden grid-cols-[minmax(0,1fr)_auto] gap-3 bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground sm:grid">
              <span>Quote line</span>
              <span>Taxability</span>
            </div>
            {calculation.line_breakdown.map((line) => {
              const override = overrides[line.line_id]
              const categoryTaxable = settings[`${line.category}_taxable`]
              return (
                <div className="grid items-center gap-3 border-t px-4 py-3 first:border-t-0 sm:grid-cols-[minmax(0,1fr)_auto]" key={line.line_id}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{line.description}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {readableLabel(line.category)} · {formatMoney(line.amount)}
                    </p>
                  </div>
                  <ReviewChoiceGroup<LineChoice>
                    disabled={editingDisabled}
                    label={`Taxability for ${line.description}`}
                    onChange={(value) => setLineOverride(line.line_id, value)}
                    options={[
                      { value: 'default', label: categoryTaxable ? 'Category: taxable' : 'Category: non-taxable' },
                      { value: 'taxable', label: 'Taxable' },
                      { value: 'exempt', label: 'Non-taxable' },
                    ]}
                    value={override === undefined ? 'default' : override ? 'taxable' : 'exempt'}
                  />
                </div>
              )
            })}
            {!calculation.line_breakdown.length ? <p className="px-4 py-8 text-center text-sm text-muted-foreground">No quote lines.</p> : null}
          </div>
        </SurfaceCard>

        <SurfaceCard title="Certificate evidence">
          {evidenceRetry ? (
            <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3 text-sm text-destructive" role="alert">
              <span>{evidenceRetry.failure.message}</span>
              <Button
                disabled={busy !== null}
                onClick={() =>
                  evidenceRetry.failure.canRetry ? void processFile(evidenceRetry.file, evidenceRetry.failure) : fileInput.current?.click()
                }
                type="button"
                variant="outline"
              >
                <RefreshCw aria-hidden="true" />
                {!evidenceRetry.failure.canRetry ? 'Choose certificate again' : evidenceRetry.failure.step === 'parse' ? 'Retry reading' : 'Retry upload'}
              </Button>
            </div>
          ) : null}
          <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">PDF, PNG, or JPEG · 10 MB maximum</p>
            <input
              accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
              aria-label="Upload exemption certificate"
              className="sr-only"
              onChange={handleFile}
              ref={fileInput}
              type="file"
            />
            <LoadingButton
              disabled={disabled}
              loading={busy === 'upload'}
              loadingText="Reading certificate..."
              onClick={() => fileInput.current?.click()}
              type="button"
              variant="outline"
            >
              <FileUp aria-hidden="true" />
              Upload certificate
            </LoadingButton>
          </div>
          {workspace.evidence.length ? (
            <div className="border-t">
              {workspace.evidence.map((evidence) => (
                <EvidenceRow evidence={evidence} key={evidence.id} onOpen={() => void openEvidence(evidence)} />
              ))}
            </div>
          ) : (
            <div className="border-t px-4 py-8 text-center text-sm text-muted-foreground">No certificate evidence.</div>
          )}
        </SurfaceCard>
      </div>

      <aside className="min-w-0">
        <SurfaceCard title="Approval summary">
          <div className="p-4">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <StatusPill tone={approvalReady ? 'success' : 'warning'}>{approvalReady ? 'Ready' : 'Review needed'}</StatusPill>
              {suggestionEvidenceId ? (
                <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:ring-violet-900">
                  Suggestions pending
                </span>
              ) : null}
            </div>
            <dl className="space-y-2.5 text-sm">
              {(
                [
                  ['Taxable', formatMoney(summary.taxable_basis)],
                  ['Exempt', formatMoney(summary.exempt_basis)],
                  ['Tax', formatMoney(summary.tax)],
                  ['Evidence', String(workspace.evidence.length)],
                ] as const
              ).map(([label, value]) => (
                <div className="flex justify-between gap-3" key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="font-semibold tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground">Exempt categories</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {summary.exemptions.length ? (
                  summary.exemptions.map((item) => (
                    <StatusPill key={item} tone="neutral">
                      {readableLabel(item)}
                    </StatusPill>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">None</span>
                )}
              </div>
            </div>
          </div>
        </SurfaceCard>
      </aside>
    </div>
  )
}
